import { Routes, Route, Navigate } from "react-router-dom";
import {
  ADMIN_BASE_PATH,
  ADMIN_DASHBOARD_PATH,
  ADMIN_USERS_PATH,
  ADMIN_STARS_RATE_PATH,
  ADMIN_MIRROR_STARS_PATH,
} from "../adminPath";
import ApiPage from "../../pages/ApiPage";
import BlogPage from "../../pages/NewsPage";
import LoginPage from "../../pages/LoginPage";
import MainPage from "../../pages/MainPage";
import PartnersPage from "../../pages/PartersPage";
import DdosSecurityPage from "../../pages/Tools/DdosSecurityPage";
import EmailCheckerPage from "../../pages/Tools/EmailCheckerPage";
import IpCheckerPage from "../../pages/Tools/IpCheckerPage";
import TelegramCheckerPage from "../../pages/Tools/TelegramCheckerPage";
import Tool1RedirectPage from "pages/Tools/Tool1RedirectPage";
import PricesPage from "../../pages/PricesPage";
import ProfileLayout from "components/Profile/ProfileLayout";
import ProfileInfo from "components/Profile/ProfileInfo";
import ProfileBalance from "components/Profile/ProfileBalance";
import ProfileSubscriptions from "components/Profile/ProfileSubscriptions";
import ProfileReferals from "components/Profile/ProfileReferals";
import ProfileSupport from "components/Profile/ProfileSupport";
import ProfileApi, { PROFILE_API_PATH } from "components/Profile/ProfileApi";
import TermsAndConditionsPage from "pages/TermsAndConditionsPage";

import { useSelector } from "react-redux";
import { RootState } from "redux/store";
import PrivacyPolicyPage from "pages/PrivacyPolicyPage";
import NewsDetails from "components/News/NewsDetails";
import NewsPage from "../../pages/NewsPage";
import NotFoundPage from "pages/NotFoundPage";
import AdminGuard from "components/Admin/AdminGuard";
import StarsRatePanel from "components/Admin/StarsRate";
import MirrorStarsPanel from "components/Admin/MirrorStars";
import ApiBlockGettingStarted from "components/API/ApiBlockLayout/ApiBlockGettingStarted";
import ApiBlockResponseExample from "components/API/ApiBlockLayout/ApiBlockResponseExample";
import { NewsPageContextProvider } from "context/NewsPageContext";
import TopUpBalancePage from "pages/TopUpBalancePage";
import InvoiceDetails from "components/InvoiceDetails";
import ContactsPage from "pages/ContactsPage";
import AdminLayout from "components/Admin/AdminLayout";
import DashboardOverview from "components/Admin/DashboardOverview";
import UserManagement from "components/Admin/UserManagement";
import AdminPage from "pages/AdminPage";
import RequireSession from "router/RequireSession";
import VerifyEmailPage from "pages/VerifyEmailPage";
import { VERIFY_EMAIL_PATH } from "utils/auth/verifyEmailToken";

export const AppRoutes = () => {
  const { isUserLoggedIn } = useSelector((state: RootState) => state.userData);

  return (
    <Routes>
      <Route path="/" element={<MainPage />} />
      <Route path="/tools/ip-check" element={<IpCheckerPage />} />
      <Route path="/tools/email-check" element={<EmailCheckerPage />} />
      <Route path="/tools/telegram-check" element={<TelegramCheckerPage />} />
      <Route path="/tools/tool1" element={<Tool1RedirectPage />} />
      <Route path="/tools/ddos-security" element={<DdosSecurityPage />} />
      <Route path="/pricing" element={<PricesPage />} />
      <Route path="/partners" element={<PartnersPage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/contacts" element={<ContactsPage />} />
      <Route path="/api-docs/" element={<ApiPage />}>
        <Route
          path="/api-docs/getting-started"
          element={<ApiBlockGettingStarted />}
        />
        <Route
          path="/api-docs/endpoints/GetIpData"
          element={<ApiBlockResponseExample />}
        />
        <Route
          path="/api-docs/endpoints/GetBalance"
          element={<ApiBlockResponseExample />}
        />
        <Route
          path="/api-docs/endpoints/GetRequestsLeft"
          element={<ApiBlockResponseExample />}
        />
        <Route
          path="/api-docs/endpoints/GetSubscriptions"
          element={<ApiBlockResponseExample />}
        />
      </Route>
      {/* Passwordless OTP sign-in (email -> code). Registration, forgot- and
          reset-password surfaces are retired — a new email is auto-registered
          on first correct code. */}
      <Route path="/auth/log-in" element={<LoginPage />} />
      {/* Cabinet: a loader while the session restores, sign-in without one
          (never a blank page). */}
      <Route
        path="/profile"
        element={
          <RequireSession>
            <ProfileLayout />
          </RequireSession>
        }
      >
        <Route
          path="/profile/info"
          element={isUserLoggedIn && <ProfileInfo />}
        />
        <Route
          path="/profile/balance"
          element={isUserLoggedIn && <ProfileBalance />}
        />
        <Route
          path="/profile/subscriptions"
          element={isUserLoggedIn && <ProfileSubscriptions />}
        />
        <Route
          path="/profile/affiliate"
          element={isUserLoggedIn && <ProfileReferals />}
        />
        <Route
          path="/profile/support"
          element={isUserLoggedIn && <ProfileSupport />}
        />
        <Route path={PROFILE_API_PATH} element={<ProfileApi />} />
        {/* The old token page moved into the API page; keep its URL working. */}
        <Route
          path="/profile/token"
          element={<Navigate to={PROFILE_API_PATH} replace />}
        />
      </Route>
      <Route
        path="/terms-and-conditions"
        element={<TermsAndConditionsPage />}
      />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route
        path="/news-all"
        element={
          <NewsPageContextProvider>
            <NewsPage />
          </NewsPageContextProvider>
        }
      />
      <Route
        path="/news/:id"
        element={
          <NewsPageContextProvider>
            <NewsDetails />
          </NewsPageContextProvider>
        }
      />
      <Route
        path="/top-up-balance"
        element={isUserLoggedIn ? <TopUpBalancePage /> : <NotFoundPage />}
      />
      <Route path="/invoice/:id" element={<InvoiceDetails />} />
      
      {/* Admin Routes — mounted at ADMIN_BASE_PATH, not the guessable /admin.
          The address is obscurity only; AdminGuard is the client-side gate and
          the backend role check remains the real one. Old /admin* URLs still
          fall through to the NotFoundPage catch-all below.

          The guard wraps the PARENT element, so a non-admin never reaches any
          child and the children need no test of their own. Note the children
          use absolute paths nested under the parent — React Router accepts this
          only because each child path is prefixed by the parent's. Leave that
          alone; it is not the thing being fixed here. */}
      <Route
        path={ADMIN_BASE_PATH}
        element={
          <AdminGuard>
            <AdminLayout />
          </AdminGuard>
        }
      >
        <Route index element={<AdminPage />} />
        <Route path={ADMIN_DASHBOARD_PATH} element={<DashboardOverview />} />
        <Route path={ADMIN_USERS_PATH} element={<UserManagement />} />
        <Route path={ADMIN_STARS_RATE_PATH} element={<StarsRatePanel />} />
        <Route path={ADMIN_MIRROR_STARS_PATH} element={<MirrorStarsPanel />} />
      </Route>
      
      {/* Public on purpose: the page itself handles a signed-out visitor, and
          opening it changes nothing until the session presses Confirm. */}
      <Route path={VERIFY_EMAIL_PATH} element={<VerifyEmailPage />} />

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};